import { useEffect, useRef } from "react";

const noiseBuffer = (ctx, brown) => {
  const len = ctx.sampleRate * 2;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    if (brown) { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.5; } else d[i] = w;
  }
  return buf;
};

const noise = (ctx, out, { brown = true, type = "lowpass", freq = 400, q = 0.7, gain = 0.3 }) => {
  const src = ctx.createBufferSource(); src.buffer = noiseBuffer(ctx, brown); src.loop = true;
  const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q;
  const g = ctx.createGain(); g.gain.value = gain;
  src.connect(f); f.connect(g); g.connect(out); src.start();
  return { src, f, g };
};

const lfo = (ctx, param, rate, depth) => {
  const o = ctx.createOscillator(); o.frequency.value = rate;
  const g = ctx.createGain(); g.gain.value = depth;
  o.connect(g); g.connect(param); o.start();
  return o;
};

const blip = (ctx, out, { f0, f1, dur, gain, type = "sine" }) => {
  const o = ctx.createOscillator(); o.type = type;
  const g = ctx.createGain();
  const p = ctx.createStereoPanner(); p.pan.value = Math.random() * 1.6 - 0.8;
  const t = ctx.currentTime;
  o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(f1, t + dur);
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(gain, t + dur * 0.25); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(p); p.connect(out); o.start(t); o.stop(t + dur + 0.05);
};

const every = (min, max, fn) => {
  let id;
  const tick = () => { fn(); id = setTimeout(tick, (min + Math.random() * (max - min)) * 1000); };
  id = setTimeout(tick, min * 1000);
  return () => clearTimeout(id);
};

const insects = (ctx, out, freq, amRate, gain) => {
  const o = ctx.createOscillator(); o.frequency.value = freq;
  const g = ctx.createGain(); g.gain.value = gain;
  const am = lfo(ctx, g.gain, amRate, gain * 0.9);
  o.connect(g); g.connect(out); o.start();
  return [o, am];
};

const SCENES = {
  highlands: (ctx, out) => {
    const wind = noise(ctx, out, { freq: 380, gain: 0.5 });
    const l = lfo(ctx, wind.f.frequency, 0.07, 220);
    const stop = every(1.5, 4.5, () => blip(ctx, out, { f0: 2300 + Math.random() * 900, f1: 3000 + Math.random() * 900, dur: 0.14, gain: 0.05 }));
    return () => { stop(); wind.src.stop(); l.stop(); };
  },
  rainforest: (ctx, out) => {
    const rain = noise(ctx, out, { brown: false, type: "bandpass", freq: 1600, q: 0.4, gain: 0.07 });
    const rumble = noise(ctx, out, { freq: 160, gain: 0.35 });
    const l = lfo(ctx, rain.g.gain, 0.11, 0.025);
    const bugs = insects(ctx, out, 4600, 28, 0.006);
    const stop = every(2.5, 6, () => blip(ctx, out, { f0: 1100 + Math.random() * 500, f1: 1500 + Math.random() * 600, dur: 0.22, gain: 0.035 }));
    return () => { stop(); rain.src.stop(); rumble.src.stop(); l.stop(); bugs.forEach((n) => n.stop()); };
  },
  night: (ctx, out) => {
    const air = noise(ctx, out, { freq: 140, gain: 0.3 });
    const a = insects(ctx, out, 4100, 22, 0.009);
    const b = insects(ctx, out, 5200, 31, 0.006);
    const stop = every(3, 7, () => blip(ctx, out, { f0: 420, f1: 300, dur: 0.16, gain: 0.05, type: "triangle" }));
    return () => { stop(); air.src.stop(); [...a, ...b].forEach((n) => n.stop()); };
  },
  ops: (ctx, out) => {
    const drone = noise(ctx, out, { freq: 110, gain: 0.5 });
    const sub = ctx.createOscillator(); sub.frequency.value = 52;
    const sg = ctx.createGain(); sg.gain.value = 0.05; const l = lfo(ctx, sg.gain, 0.5, 0.04);
    sub.connect(sg); sg.connect(out); sub.start();
    const stop = every(4, 9, () => blip(ctx, out, { f0: 1800, f1: 1750, dur: 0.05, gain: 0.03, type: "square" }));
    return () => { stop(); drone.src.stop(); sub.stop(); l.stop(); };
  },
  vineyard: (ctx, out) => {
    // warm summer wind, cicada chorus, and the occasional drip echoing in a stone cellar
    const wind = noise(ctx, out, { freq: 520, gain: 0.3 });
    const l = lfo(ctx, wind.f.frequency, 0.05, 260);
    const echo = ctx.createDelay(1.0); echo.delayTime.value = 0.42;
    const fb = ctx.createGain(); fb.gain.value = 0.45;
    const wet = ctx.createGain(); wet.gain.value = 0.6;
    echo.connect(fb); fb.connect(echo); echo.connect(wet); wet.connect(out);
    const cicadas = [[4200, 46, 0.007], [5100, 58, 0.005], [3600, 39, 0.006]].map(([f, r, g]) => insects(ctx, out, f, r, g)).flat();
    const swell = lfo(ctx, out.gain, 0.09, 0.08);
    const stopDrip = every(3, 8, () => blip(ctx, echo, { f0: 1500 + Math.random() * 600, f1: 900, dur: 0.09, gain: 0.05 }));
    return () => { stopDrip(); wind.src.stop(); l.stop(); swell.stop(); cicadas.forEach((n) => n.stop()); };
  }
};

const corkPop = (ctx, out) => {
  const t = ctx.currentTime;
  const src = ctx.createBufferSource(); src.buffer = noiseBuffer(ctx, false);
  const f = ctx.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 900; f.Q.value = 1.2;
  const g = ctx.createGain(); g.gain.setValueAtTime(0.6, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
  src.connect(f); f.connect(g); g.connect(out); src.start(t); src.stop(t + 0.15);
  const o = ctx.createOscillator(); o.frequency.setValueAtTime(190, t); o.frequency.exponentialRampToValueAtTime(70, t + 0.18);
  const og = ctx.createGain(); og.gain.setValueAtTime(0.35, t); og.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
  o.connect(og); og.connect(out); o.start(t); o.stop(t + 0.22);
  setTimeout(() => ref_fizz(ctx, out), 180);
};

const ref_fizz = (ctx, out) => {
  const t = ctx.currentTime;
  const src = ctx.createBufferSource(); src.buffer = noiseBuffer(ctx, false);
  const f = ctx.createBiquadFilter(); f.type = "highpass"; f.frequency.value = 5000;
  const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(0.06, t + 0.1); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
  src.connect(f); f.connect(g); g.connect(out); src.start(t); src.stop(t + 1.7);
};

// Synthesised ambient soundscape (Web Audio, no assets). Returns a cue() for scene changes.
export function useAmbience(kind, enabled) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled || !SCENES[kind]) return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;
    const ctx = new Ctx();
    const master = ctx.createGain(); master.gain.value = 0; master.connect(ctx.destination);
    master.gain.linearRampToValueAtTime(0.7, ctx.currentTime + 2.5);
    const teardown = SCENES[kind](ctx, master);
    ref.current = { ctx, master };
    return () => {
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
      setTimeout(() => { teardown(); ctx.close(); }, 700);
      ref.current = null;
    };
  }, [kind, enabled]);

  return (event = "scene") => {
    const a = ref.current;
    if (!a) return;
    if (event === "final" && kind === "vineyard") corkPop(a.ctx, a.master);
    else blip(a.ctx, a.master, { f0: 880, f1: 660, dur: 0.35, gain: 0.05 });
  };
}
